import type { BookPerson, RemoteConversation } from "./api.ts";

/*
 * « Discuter avec… » : converser avec une personne du livre, repris de Studio (dépôt ai,
 * `components/studio/StudioChat.tsx`, `ReaderChatPanel.tsx`, `stageDirections.ts`).
 */

/** Nombre de personnes proposées quand le back-office n'en fixe pas (réglage `max_persons`). */
export const DEFAULT_MAX_PERSONS = 8;

/**
 * Les personnes déjà rencontrées : celles qui apparaissent plus loin que le lecteur seraient un spoiler.
 * `reached(href)` dit si le lecteur est arrivé à ce fichier ; une personne sans fichier connu est proposée.
 */
export function personsMet(
  persons: BookPerson[],
  reached: (href: string) => boolean,
  max: number | null | undefined,
): BookPerson[] {
  return persons.filter((person) => !person.first_href || reached(person.first_href)).slice(0, max ?? DEFAULT_MAX_PERSONS);
}

/** Le dialogue déjà ouvert avec cette personne sur ce livre, à reprendre plutôt qu'à recommencer. */
export function findCharacterConversation(
  conversations: RemoteConversation[],
  productId: string,
  name: string,
): RemoteConversation | undefined {
  return conversations.find(
    (c) => c.kind === "character" && c.character?.name === name && c.product_ids.includes(productId),
  );
}

/**
 * Dans un dialogue, les gestes que la personne décrit (« *Soupirant* Ah… ») s'écrivent entre crochets,
 * pas en italique : [Soupirant] Ah… Seule une italique qui ouvre un paragraphe en est un.
 */
export function bracketStageDirections(content: string): string {
  return content.replace(/^(\s*)(\*|_)(?!\2)([^*_\n]+?)\2(?!\2)/gm, (_, space: string, __, text: string) => `${space}[${text.trim()}]`);
}

/** Icône d'une personne selon ce que le livre dit d'elle. */
export const personIcon = (person: BookPerson) =>
  person.gender === "femme" ? "female" : person.gender === "homme" ? "male" : "person";
